import React, { useEffect, useState, useRef } from "react";
import { motion, useInView, AnimatePresence } from "framer-motion";
import axios from "axios";
import { Link } from "react-router-dom";
import {
  Cpu, GraduationCap, ShieldCheck, BarChart3, Users, Clock,
  CheckCircle2, ArrowRight, ChevronDown, Sparkles, Building2,
  FileText, ScanLine, Lock, Heart, AlertTriangle, HardHat,
  Megaphone, BookOpen, Award, Zap, Globe, Mail, Phone, Play,
} from "lucide-react";
import "./Landing.css";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/* ------------------------------------------------------------------ */
/* Animaciones genéricas                                              */
/* ------------------------------------------------------------------ */
const fadeUp = {
  hidden: { opacity: 0, y: 28 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.7, ease: [0.21, 0.47, 0.32, 0.98] } },
};
const stagger = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.08 } },
};

const Reveal = ({ children, delay = 0, className = "" }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-60px" });
  return (
    <motion.div
      ref={ref}
      className={className}
      initial="hidden"
      animate={inView ? "visible" : "hidden"}
      variants={fadeUp}
      transition={{ delay }}
    >
      {children}
    </motion.div>
  );
};

/* Counter animado */
const Counter = ({ to, suffix = "", duration = 1.8 }) => {
  const [v, setV] = useState(0);
  const ref = useRef(null);
  const inView = useInView(ref, { once: true });
  useEffect(() => {
    if (!inView) return;
    let raf;
    const start = performance.now();
    const tick = (t) => {
      const p = Math.min((t - start) / (duration * 1000), 1);
      const eased = 1 - Math.pow(1 - p, 3);
      setV(Math.round(to * eased));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [inView, to, duration]);
  return <span ref={ref}>{v}{suffix}</span>;
};

/* ------------------------------------------------------------------ */
/* HERO                                                               */
/* ------------------------------------------------------------------ */
const Hero = ({ onCta, onDemo }) => (
  <section className="lp-hero" data-testid="lp-hero">
    <div className="lp-hero-grid" />
    <div className="lp-hero-glow" />
    <div className="lp-container">
      <motion.div
        className="lp-hero-content"
        initial="hidden"
        animate="visible"
        variants={stagger}
      >
        <motion.span className="lp-kicker" variants={fadeUp}>
          <Sparkles size={14} /> Plataforma integral para Ayuntamientos
        </motion.span>
        <motion.h1 variants={fadeUp}>
          Concello<span className="lp-360">360</span>
        </motion.h1>
        <motion.p className="lp-hero-tagline" variants={fadeUp}>
          Formación, Inteligencia Artificial y Modernización Digital
          para Ayuntamientos
        </motion.p>
        <motion.p className="lp-hero-lead" variants={fadeUp}>
          La plataforma integral que ayuda a los ayuntamientos a formar a su personal,
          cumplir con las obligaciones normativas y aprovechar el potencial de la
          inteligencia artificial desde un único entorno.
        </motion.p>
        <motion.div className="lp-hero-ctas" variants={fadeUp}>
          <button className="lp-btn lp-btn-primary" onClick={onCta} data-testid="lp-cta-hero-demo">
            Solicitar demostración gratuita
            <ArrowRight size={18} />
          </button>
          <button className="lp-btn lp-btn-ghost" onClick={onDemo} data-testid="lp-cta-hero-play">
            <Play size={16} fill="currentColor" /> Probar la plataforma
          </button>
        </motion.div>
        <motion.div className="lp-hero-trust" variants={fadeUp}>
          <div><Counter to={48} />+<small>cursos</small></div>
          <div><Counter to={1008} /><small>horas formativas</small></div>
          <div><Counter to={192} /><small>SCORMs</small></div>
          <div><Counter to={6} /><small>bloques temáticos</small></div>
        </motion.div>
      </motion.div>

      <motion.div
        className="lp-hero-visual"
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay: 0.4, duration: 0.8 }}
      >
        <div className="lp-screen">
          <div className="lp-screen-bar">
            <span /><span /><span />
            <div className="lp-screen-url">aula.aprendendo.es</div>
          </div>
          <div className="lp-screen-body">
            <div className="lp-screen-side">
              <div className="lp-side-item active"><GraduationCap size={14}/> Cursos</div>
              <div className="lp-side-item"><Cpu size={14}/> Asistente IA</div>
              <div className="lp-side-item"><BarChart3 size={14}/> Informes</div>
              <div className="lp-side-item"><Users size={14}/> Personal</div>
              <div className="lp-side-item"><Award size={14}/> Certificados</div>
            </div>
            <div className="lp-screen-main">
              <div className="lp-screen-card">
                <div className="lp-card-thumb thumb-a" />
                <strong>Administración electrónica</strong>
                <small>21 h · 4 temas SCORM</small>
                <div className="lp-progress"><span style={{width:"68%"}}/></div>
              </div>
              <div className="lp-screen-card">
                <div className="lp-card-thumb thumb-b" />
                <strong>Igualdad y RR.HH.</strong>
                <small>21 h · examen final</small>
                <div className="lp-progress"><span style={{width:"42%"}}/></div>
              </div>
              <div className="lp-screen-card">
                <div className="lp-card-thumb thumb-c" />
                <strong>Sistema VioGén</strong>
                <small>Policía Local</small>
                <div className="lp-progress"><span style={{width:"100%"}}/></div>
              </div>
            </div>
          </div>
        </div>
        <motion.div
          className="lp-float lp-float-1"
          animate={{ y: [0, -8, 0] }}
          transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
        >
          <Award size={20} />
          <div>
            <strong>Certificado emitido</strong>
            <small>Concello de Lugo · ahora</small>
          </div>
        </motion.div>
        <motion.div
          className="lp-float lp-float-2"
          animate={{ y: [0, 8, 0] }}
          transition={{ duration: 4.5, repeat: Infinity, ease: "easeInOut", delay: 0.3 }}
        >
          <Cpu size={20} />
          <div>
            <strong>IA en línea</strong>
            <small>«Redacta una memoria…»</small>
          </div>
        </motion.div>
      </motion.div>
    </div>
  </section>
);

/* ------------------------------------------------------------------ */
/* Retos                                                              */
/* ------------------------------------------------------------------ */
const Retos = () => {
  const items = [
    { Icon: GraduationCap, t: "Formación obligatoria del personal" },
    { Icon: Globe, t: "Administración electrónica cada vez más compleja" },
    { Icon: ShieldCheck, t: "Nuevas exigencias en protección de datos y ciberseguridad" },
    { Icon: Cpu, t: "Necesidad de incorporar herramientas de inteligencia artificial" },
    { Icon: Clock, t: "Falta de tiempo para coordinar acciones formativas" },
    { Icon: Users, t: "Escasez de recursos técnicos y humanos" },
  ];
  return (
    <section className="lp-section lp-section-dark" data-testid="lp-retos">
      <div className="lp-container">
        <Reveal>
          <span className="lp-eyebrow">Los retos</span>
          <h2 className="lp-h2">La plataforma que impulsa el futuro de los ayuntamientos</h2>
          <p className="lp-h2-sub">Los ayuntamientos afrontan cada día nuevos retos.<br/>Concello360 nace para darles respuesta.</p>
        </Reveal>
        <motion.div
          className="lp-retos-grid"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-80px" }}
          variants={stagger}
        >
          {items.map(({ Icon, t }, i) => (
            <motion.div key={i} className="lp-reto-card" variants={fadeUp}>
              <Icon size={22} />
              <span>{t}</span>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

/* ------------------------------------------------------------------ */
/* Qué es                                                             */
/* ------------------------------------------------------------------ */
const QueEs = () => {
  const features = [
    "Formación continua", "Capacitación digital",
    "Inteligencia artificial aplicada", "Cumplimiento normativo",
    "Certificación y seguimiento", "Informes y estadísticas",
    "Modernización administrativa",
  ];
  return (
    <section className="lp-section lp-bg-light" data-testid="lp-quees">
      <div className="lp-container lp-two">
        <Reveal>
          <span className="lp-eyebrow">¿Qué es Concello360?</span>
          <h2 className="lp-h2">
            Una plataforma integral diseñada<br/>
            <em>específicamente</em> para administraciones locales.
          </h2>
          <p className="lp-text">
            Centralizamos en un único entorno todo lo que hoy se hace por separado:
            formación, IA aplicada, cumplimiento normativo, certificación y reporting.
            Una sola plataforma. Una única cuota.
          </p>
        </Reveal>
        <Reveal delay={0.15}>
          <div className="lp-feat-list">
            {features.map((f, i) => (
              <motion.div
                key={f}
                className="lp-feat-item"
                initial={{ opacity: 0, x: -16 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.07, duration: 0.5 }}
              >
                <CheckCircle2 size={18} className="lp-check" />
                <span>{f}</span>
              </motion.div>
            ))}
          </div>
        </Reveal>
      </div>
    </section>
  );
};

/* ------------------------------------------------------------------ */
/* Bloques formativos                                                 */
/* ------------------------------------------------------------------ */
const BLOQUES = [
  {
    Icon: Building2, title: "Administración General",
    items: ["Administración electrónica", "Expediente electrónico", "Firma digital",
            "Transparencia", "Atención a la ciudadanía", "Procedimiento administrativo",
            "Archivo electrónico"],
    color: "blue",
  },
  {
    Icon: Users, title: "Recursos Humanos",
    items: ["Igualdad", "Prevención del acoso laboral", "Liderazgo",
            "Competencias digitales", "Gestión de equipos", "Comunicación interna"],
    color: "violet",
  },
  {
    Icon: ShieldCheck, title: "Policía Local",
    items: ["Violencia de género", "Sistema VioGén", "Drones policiales",
            "Cartografía digital", "Tráfico y seguridad vial", "Actuación policial especializada"],
    color: "navy",
  },
  {
    Icon: AlertTriangle, title: "Protección Civil y Emergencias",
    items: ["Coordinación operativa", "Gestión de emergencias",
            "Cartografía y geolocalización", "Intervención en incidentes"],
    color: "amber",
  },
  {
    Icon: Heart, title: "Servicios Sociales",
    items: ["Atención a colectivos vulnerables", "Menores",
            "Violencia vicaria y económica", "Intervención social especializada"],
    color: "rose",
  },
  {
    Icon: HardHat, title: "Obras y Servicios",
    items: ["Prevención de riesgos laborales", "Seguridad en trabajos específicos",
            "Buenas prácticas operativas", "Gestión de incidencias"],
    color: "emerald",
  },
];

const Bloques = () => (
  <section className="lp-section lp-section-dark" data-testid="lp-bloques">
    <div className="lp-container">
      <Reveal>
        <span className="lp-eyebrow">Catálogo formativo</span>
        <h2 className="lp-h2">Formación para todo el ayuntamiento</h2>
        <p className="lp-h2-sub">
          6 bloques temáticos · 48 cursos · 1.008 horas · 192 SCORMs en Moodle
        </p>
      </Reveal>
      <motion.div
        className="lp-bloques-grid"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: "-100px" }}
        variants={stagger}
      >
        {BLOQUES.map(({ Icon, title, items, color }, i) => (
          <motion.div key={i} className={`lp-bloque-card lp-bloque-${color}`} variants={fadeUp}>
            <div className="lp-bloque-head">
              <Icon size={22} />
              <h3>{title}</h3>
            </div>
            <ul>
              {items.map(t => <li key={t}>{t}</li>)}
            </ul>
          </motion.div>
        ))}
      </motion.div>
    </div>
  </section>
);

/* ------------------------------------------------------------------ */
/* IA Section                                                         */
/* ------------------------------------------------------------------ */
const SeccionIA = ({ onCta }) => {
  const items = [
    { Icon: FileText, t: "Generar documentos administrativos" },
    { Icon: BookOpen, t: "Redactar informes y memorias" },
    { Icon: ScanLine, t: "Consultar normativa al instante" },
    { Icon: Megaphone, t: "Resolver dudas procedimentales" },
    { Icon: Zap, t: "Mejorar la productividad diaria" },
    { Icon: Cpu, t: "Asistentes inteligentes de aprendizaje" },
  ];
  return (
    <section className="lp-section lp-ia" data-testid="lp-ia">
      <div className="lp-container lp-two-50">
        <Reveal>
          <span className="lp-eyebrow lp-eyebrow-on-dark">
            <Sparkles size={12} /> Inteligencia Artificial
          </span>
          <h2 className="lp-h2 lp-h2-on-dark">
            IA aplicada al trabajo diario del empleado público.
          </h2>
          <p className="lp-text-on-dark">
            La Administración Pública está cambiando. Concello360 incorpora
            herramientas de Inteligencia Artificial adaptadas al entorno municipal,
            con un enfoque <strong>práctico, seguro y orientado al trabajo diario</strong>.
          </p>
          <button className="lp-btn lp-btn-accent" onClick={onCta} data-testid="lp-cta-ia">
            Ver la IA en acción <ArrowRight size={18} />
          </button>
        </Reveal>
        <Reveal delay={0.1}>
          <div className="lp-ia-grid">
            {items.map(({ Icon, t }, i) => (
              <motion.div
                key={i}
                className="lp-ia-card"
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.06, duration: 0.4 }}
              >
                <Icon size={20} />
                <span>{t}</span>
              </motion.div>
            ))}
          </div>
        </Reveal>
      </div>
    </section>
  );
};

/* ------------------------------------------------------------------ */
/* Beneficios                                                         */
/* ------------------------------------------------------------------ */
const BENEFITS = [
  { Icon: Clock, title: "Ahorro de tiempo",
    desc: "Centralice toda la formación y gestión en una única plataforma." },
  { Icon: GraduationCap, title: "Mayor capacitación",
    desc: "Mejore las competencias de toda la plantilla municipal." },
  { Icon: ShieldCheck, title: "Cumplimiento normativo",
    desc: "Mantenga actualizada la formación obligatoria de los empleados públicos." },
  { Icon: Sparkles, title: "Transformación digital",
    desc: "Impulse la modernización de la organización." },
  { Icon: BarChart3, title: "Información en tiempo real",
    desc: "Informes, estadísticas y seguimiento automatizado." },
  { Icon: Lock, title: "Servicio integral",
    desc: "Un único proveedor para formación, digitalización e inteligencia artificial." },
];

const Beneficios = () => (
  <section className="lp-section lp-bg-light" data-testid="lp-beneficios">
    <div className="lp-container">
      <Reveal>
        <span className="lp-eyebrow">Por qué Concello360</span>
        <h2 className="lp-h2">Beneficios para el ayuntamiento</h2>
      </Reveal>
      <motion.div
        className="lp-benef-grid"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: "-80px" }}
        variants={stagger}
      >
        {BENEFITS.map(({ Icon, title, desc }, i) => (
          <motion.div key={i} className="lp-benef-card" variants={fadeUp}>
            <div className="lp-benef-icon"><Icon size={22} /></div>
            <h4>{title}</h4>
            <p>{desc}</p>
          </motion.div>
        ))}
      </motion.div>
    </div>
  </section>
);

/* ------------------------------------------------------------------ */
/* Demo funcional                                                     */
/* ------------------------------------------------------------------ */
const DEMO_COURSE = "expediente-electronico";
const DEMO_TEMAS = [
  { key: "tema-1-fundamentos", label: "Tema 1 · Fundamentos del expediente electrónico" },
  { key: "tema-2-documentos-firma", label: "Tema 2 · Documentos y firma electrónica" },
  { key: "tema-3-tramitacion", label: "Tema 3 · Tramitación y gestión" },
  { key: "tema-4-archivo-casos", label: "Tema 4 · Archivo, conservación y casos reales" },
];

const DemoFuncional = React.forwardRef((_, ref) => {
  const [active, setActive] = useState(DEMO_TEMAS[0].key);
  const previewUrl = `${API}/courses/${DEMO_COURSE}/preview/${active}/`;
  return (
    <section className="lp-section lp-demo" ref={ref} data-testid="lp-demo">
      <div className="lp-container">
        <Reveal>
          <span className="lp-eyebrow lp-eyebrow-on-dark">Demo funcional · sin registro</span>
          <h2 className="lp-h2 lp-h2-on-dark">
            Pruebe ahora mismo un curso completo.
          </h2>
          <p className="lp-text-on-dark" style={{ maxWidth: 620 }}>
            «Expediente Electrónico» es uno de los 48 cursos del catálogo, con sus
            4 temas SCORM 1.2, examen final tipo test y seguimiento automático a Moodle.
            Hemos integrado los 4 temas para que pueda navegarlos directamente.
          </p>
        </Reveal>
        <Reveal delay={0.1}>
          <div className="lp-demo-tabs">
            {DEMO_TEMAS.map(t => (
              <button
                key={t.key}
                className={`lp-demo-tab ${active === t.key ? "active" : ""}`}
                onClick={() => setActive(t.key)}
                data-testid={`lp-demo-tab-${t.key}`}
              >
                {t.label}
              </button>
            ))}
          </div>
          <div className="lp-demo-frame" data-testid="lp-demo-frame">
            <iframe
              key={active}
              src={previewUrl}
              title="Demo Concello360"
              loading="lazy"
            />
          </div>
          <div className="lp-demo-foot">
            <small>
              <Sparkles size={12} /> Navegando una previsualización 1:1 del contenido SCORM
              real que se entrega a su Moodle.
            </small>
          </div>
        </Reveal>
      </div>
    </section>
  );
});

/* ------------------------------------------------------------------ */
/* Cómo funciona                                                      */
/* ------------------------------------------------------------------ */
const PASOS = [
  { n: "01", title: "Implantación", desc: "Configuramos la plataforma para su ayuntamiento." },
  { n: "02", title: "Activación", desc: "Damos de alta a los usuarios y departamentos." },
  { n: "03", title: "Formación", desc: "Los empleados acceden desde cualquier dispositivo." },
  { n: "04", title: "Seguimiento", desc: "Estadísticas e informes automáticos en tiempo real." },
  { n: "05", title: "Mejora continua", desc: "Actualizamos contenidos, herramientas y funcionalidades." },
];

const ComoFunciona = () => (
  <section className="lp-section" data-testid="lp-comofunciona">
    <div className="lp-container">
      <Reveal>
        <span className="lp-eyebrow">Cómo funciona</span>
        <h2 className="lp-h2">De la implantación al impacto, en 5 pasos.</h2>
      </Reveal>
      <div className="lp-pasos">
        {PASOS.map((p, i) => (
          <motion.div
            key={p.n}
            className="lp-paso"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: i * 0.12, duration: 0.55 }}
          >
            <div className="lp-paso-n">{p.n}</div>
            <h4>{p.title}</h4>
            <p>{p.desc}</p>
          </motion.div>
        ))}
      </div>
    </div>
  </section>
);

/* ------------------------------------------------------------------ */
/* Planes                                                             */
/* ------------------------------------------------------------------ */
const PLANES = [
  {
    name: "Concello Base",
    sub: "Hasta 20 usuarios",
    price: "399 €",
    unit: "/mes",
    items: ["Plataforma completa", "Catálogo formativo general",
            "Certificados automáticos", "Informes básicos", "IA de aprendizaje",
            "Cursos a medida sin coste añadido"],
  },
  {
    name: "Concello Plus",
    sub: "Hasta 100 usuarios",
    price: "799 €",
    unit: "/mes",
    featured: true,
    badge: "Más elegido",
    items: ["Todo lo del plan Base", "Estadísticas avanzadas",
            "Videoconferencias integradas", "Rutas formativas",
            "Herramientas colaborativas",
            "Cursos a medida sin coste añadido"],
  },
  {
    name: "Concello Premium",
    sub: "Más de 100 usuarios",
    price: "A medida",
    unit: "",
    items: ["Usuarios ampliados", "Formación personalizada",
            "Consultoría especializada", "Automatización documental",
            "IA avanzada", "Soporte prioritario",
            "Cursos a medida ilimitados"],
  },
];

const Planes = ({ onCta }) => (
  <section className="lp-section lp-bg-light" id="planes" data-testid="lp-planes">
    <div className="lp-container">
      <Reveal>
        <span className="lp-eyebrow">Planes disponibles</span>
        <h2 className="lp-h2">Una cuota mensual. Cero sorpresas.</h2>
        <p className="lp-h2-sub">
          Elija el plan que mejor se adapte al tamaño de su plantilla.
          Sin permanencia. Sin licencias por usuario opacas.
        </p>
      </Reveal>
      <div className="lp-planes-grid">
        {PLANES.map((p, i) => (
          <motion.div
            key={p.name}
            className={`lp-plan ${p.featured ? "lp-plan-featured" : ""}`}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: i * 0.1, duration: 0.55 }}
          >
            {p.badge && <span className="lp-plan-badge">{p.badge}</span>}
            <header>
              <h3>{p.name}</h3>
              <small>{p.sub}</small>
            </header>
            <div className="lp-plan-price">
              <span className="lp-plan-amount">
                {p.price === "A medida" ? "" : "Desde "}
                <strong>{p.price}</strong>
              </span>
              <span className="lp-plan-unit">{p.unit}</span>
            </div>
            <ul>
              {p.items.map(t => (
                <li key={t}><CheckCircle2 size={16} className="lp-check"/> {t}</li>
              ))}
            </ul>
            <button
              className={`lp-btn ${p.featured ? "lp-btn-primary" : "lp-btn-outline"}`}
              onClick={() => onCta(p.name)}
              data-testid={`lp-plan-cta-${p.name.toLowerCase().replace(/\s+/g, "-")}`}
            >
              {p.price === "A medida" ? "Solicitar presupuesto" : "Empezar prueba"}
              <ArrowRight size={16} />
            </button>
          </motion.div>
        ))}
      </div>

      <motion.div
        className="lp-custom-banner"
        data-testid="lp-custom-banner"
        initial={{ opacity: 0, y: 24 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-60px" }}
        transition={{ duration: 0.6 }}
      >
        <div className="lp-custom-banner-icon">
          <Sparkles size={28} />
        </div>
        <div className="lp-custom-banner-body">
          <span className="lp-eyebrow">Incluido en la cuota</span>
          <h3>Cursos a medida para su ayuntamiento, sin coste añadido.</h3>
          <p>
            Diseñamos formaciones específicas para cubrir las necesidades reales
            de cada administración: nuevas ordenanzas, procesos internos,
            herramientas propias, retos de su plantilla. Esos cursos se
            integran <strong>dentro de su pack de suscripción mensual</strong>,
            sin coste adicional.
          </p>
          <button
            className="lp-btn lp-btn-primary"
            onClick={() => onCta("Cursos a medida")}
            data-testid="lp-custom-banner-cta"
          >
            Hablar de un curso a medida <ArrowRight size={16} />
          </button>
        </div>
      </motion.div>
    </div>
  </section>
);

/* ------------------------------------------------------------------ */
/* Por qué elegirnos                                                  */
/* ------------------------------------------------------------------ */
const PorQue = () => {
  const items = [
    "Formación para Administraciones Públicas",
    "Plataformas de teleformación",
    "Inteligencia Artificial aplicada",
    "Administración electrónica",
    "Seguridad y emergencias",
    "Policía Local",
    "Protección Civil",
    "Gestión de proyectos formativos públicos",
  ];
  return (
    <section className="lp-section" data-testid="lp-porque">
      <div className="lp-container lp-two">
        <Reveal>
          <span className="lp-eyebrow">¿Por qué elegir Concello360?</span>
          <h2 className="lp-h2">Porque entendemos la Administración Local.</h2>
          <p className="lp-text">
            No ofrecemos cursos aislados. Ofrecemos una solución integral para
            ayudar a los ayuntamientos a formar mejor a sus empleados, modernizar
            su organización y prepararse para los retos del futuro.
          </p>
          <p className="lp-text">
            Nuestro equipo cuenta con experiencia real en:
          </p>
        </Reveal>
        <Reveal delay={0.1}>
          <ul className="lp-pillars">
            {items.map((x, i) => (
              <motion.li
                key={x}
                initial={{ opacity: 0, x: -10 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.06 }}
              >
                <CheckCircle2 size={18} className="lp-check"/>
                {x}
              </motion.li>
            ))}
          </ul>
        </Reveal>
      </div>
    </section>
  );
};

/* ------------------------------------------------------------------ */
/* CTA final + formulario                                             */
/* ------------------------------------------------------------------ */
const CtaFinal = ({ open, prefill, onClose }) => {
  const [form, setForm] = useState({
    name: "", email: "", organization: "", role: "", phone: "",
    employees: "", message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (prefill?.plan) {
      setForm(f => ({ ...f, message: `Estoy interesado en el plan ${prefill.plan}.` }));
    }
  }, [prefill?.plan]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    setError(null);
    if (!form.name.trim() || !form.email.trim() || !form.organization.trim()) {
      setError("Nombre, email y ayuntamiento son obligatorios.");
      return;
    }
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email.trim())) {
      setError("Email no válido.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/leads`, {
        ...form,
        course_id: null,
        course_title: prefill?.plan ? `Plan ${prefill.plan}` : null,
        source: prefill?.source || "landing",
      });
      setDone(true);
    } catch (err) {
      setError("No hemos podido enviar tu solicitud. Inténtalo más tarde.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="lp-modal-back"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
          data-testid="lp-modal"
        >
          <motion.div
            className="lp-modal"
            initial={{ y: 24, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 24, opacity: 0 }}
            onClick={e => e.stopPropagation()}
          >
            {done ? (
              <div className="lp-modal-thanks" data-testid="lp-modal-thanks">
                <div className="lp-thanks-check">
                  <CheckCircle2 size={48} />
                </div>
                <h3>Gracias, {form.name.split(" ")[0]}.</h3>
                <p>
                  Hemos recibido tu solicitud. Nos pondremos en contacto contigo
                  en menos de <strong>48 horas laborables</strong> para preparar
                  una demostración personalizada para <strong>{form.organization}</strong>.
                </p>
                <button className="lp-btn lp-btn-primary" onClick={onClose} data-testid="lp-modal-close-thanks">
                  Volver a la página
                </button>
              </div>
            ) : (
              <>
                <header>
                  <h3>Solicitar demostración gratuita</h3>
                  <button className="lp-modal-x" onClick={onClose} aria-label="Cerrar" data-testid="lp-modal-close">×</button>
                </header>
                <p className="lp-modal-lead">
                  Le mostramos cómo funciona Concello360 en una sesión personalizada
                  para su ayuntamiento. Sin compromiso.
                </p>
                <form onSubmit={submit} className="lp-modal-form" data-testid="lp-modal-form">
                  <div className="lp-form-grid">
                    <label>
                      <span>Nombre y apellidos *</span>
                      <input type="text" value={form.name} onChange={set("name")} required data-testid="lp-form-name"/>
                    </label>
                    <label>
                      <span>Email corporativo *</span>
                      <input type="email" value={form.email} onChange={set("email")} required data-testid="lp-form-email"/>
                    </label>
                    <label>
                      <span>Ayuntamiento / Entidad *</span>
                      <input type="text" value={form.organization} onChange={set("organization")} required data-testid="lp-form-org"/>
                    </label>
                    <label>
                      <span>Cargo o puesto</span>
                      <input type="text" value={form.role} onChange={set("role")} data-testid="lp-form-role"/>
                    </label>
                    <label>
                      <span>Teléfono</span>
                      <input type="tel" value={form.phone} onChange={set("phone")} data-testid="lp-form-phone"/>
                    </label>
                    <label>
                      <span>Personas a formar</span>
                      <select value={form.employees} onChange={set("employees")} data-testid="lp-form-employees">
                        <option value="">Selecciona…</option>
                        <option value="1-20">1 a 20</option>
                        <option value="21-100">21 a 100</option>
                        <option value="100+">Más de 100</option>
                      </select>
                    </label>
                  </div>
                  <label className="lp-form-msg">
                    <span>¿Qué necesitas? (opcional)</span>
                    <textarea rows={4} value={form.message} onChange={set("message")} data-testid="lp-form-message"
                      placeholder="Cuéntanos contexto del proyecto, fechas, integración con vuestro Moodle…"
                    />
                  </label>
                  {error && <div className="lp-form-error" data-testid="lp-form-error">{error}</div>}
                  <button type="submit" className="lp-btn lp-btn-primary lp-btn-block" disabled={submitting} data-testid="lp-form-submit">
                    {submitting ? "Enviando…" : "Enviar solicitud"}
                    {!submitting && <ArrowRight size={18}/>}
                  </button>
                  <p className="lp-form-privacy">
                    <Lock size={12}/> Datos tratados según RGPD. Solo para responder a esta petición.
                  </p>
                </form>
              </>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

/* ------------------------------------------------------------------ */
/* CTA banda                                                          */
/* ------------------------------------------------------------------ */
const CtaBanda = ({ onCta }) => (
  <section className="lp-section lp-cta-band" data-testid="lp-cta-band">
    <div className="lp-container">
      <Reveal>
        <h2 className="lp-h2 lp-h2-on-dark">Solicite una demostración gratuita</h2>
        <p className="lp-text-on-dark" style={{ maxWidth: 720 }}>
          Descubra cómo Concello360 puede ayudar a su ayuntamiento a mejorar la
          capacitación, simplificar la gestión formativa e incorporar IA de
          forma sencilla y eficaz.
        </p>
        <button className="lp-btn lp-btn-accent lp-btn-lg" onClick={onCta} data-testid="lp-cta-band-btn">
          Reservar mi demostración <ArrowRight size={18}/>
        </button>
      </Reveal>
    </div>
  </section>
);

/* ------------------------------------------------------------------ */
/* Footer                                                             */
/* ------------------------------------------------------------------ */
const FooterLP = () => (
  <footer className="lp-foot" data-testid="lp-footer">
    <div className="lp-container lp-foot-grid">
      <div>
        <div className="lp-brand">
          <div className="lp-logo">
            <img src="/aprendendo-logo.png" alt="Aprendendo" />
          </div>
          <div>Concello<span className="lp-360">360</span><small>La plataforma que impulsa el futuro de los ayuntamientos.</small></div>
        </div>
        <p className="lp-foot-by">
          Una solución de <a href="https://www.aprendendo.es" target="_blank" rel="noreferrer"><strong>Aprendendo</strong></a>
        </p>
      </div>
      <div>
        <h5>APRENDENDO</h5>
        <p>Formación y Transformación Digital para Administraciones Públicas.<br/>
        Con sede en Villagarcía de Arosa, damos cobertura personalizada en toda Galicia.</p>
      </div>
      <div>
        <h5>Contacto</h5>
        <p>
          <a href="mailto:info@aprendendo.es"><Mail size={14}/> info@aprendendo.es</a><br/>
          <a href="tel:+34626343239"><Phone size={14}/> 626 343 239</a><br/>
          <a href="https://www.aprendendo.es" target="_blank" rel="noreferrer"><Globe size={14}/> www.aprendendo.es</a>
        </p>
      </div>
      <div>
        <h5>Plataforma</h5>
        <p>
          <Link to="/catalogo">Catálogo de cursos</Link><br/>
          <a href="#planes">Planes y precios</a>
        </p>
      </div>
    </div>
    <div className="lp-foot-bottom">
      © {new Date().getFullYear()} Concello360 · Aprendendo S.L. Todos los derechos reservados.
    </div>
  </footer>
);

/* ------------------------------------------------------------------ */
/* Nav                                                                */
/* ------------------------------------------------------------------ */
const NavLP = ({ onCta }) => {
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const h = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", h);
    return () => window.removeEventListener("scroll", h);
  }, []);
  return (
    <nav className={`lp-nav ${scrolled ? "scrolled" : ""}`} data-testid="lp-nav">
      <div className="lp-container">
        <a href="#" className="lp-brand">
          <div className="lp-logo">
            <img src="/aprendendo-logo.png" alt="Aprendendo" />
          </div>
          <span>Concello<span className="lp-360">360</span></span>
        </a>
        <div className="lp-nav-links">
          <a href="#lp-retos" data-testid="lp-nav-retos">Retos</a>
          <a href="#lp-bloques" data-testid="lp-nav-cursos">Cursos</a>
          <a href="#lp-ia" data-testid="lp-nav-ia">IA</a>
          <a href="#lp-demo" data-testid="lp-nav-demo">Demo</a>
          <a href="#planes" data-testid="lp-nav-planes">Planes</a>
        </div>
        <button className="lp-btn lp-btn-primary lp-btn-sm" onClick={onCta} data-testid="lp-nav-cta">
          Solicitar demo
        </button>
      </div>
    </nav>
  );
};

/* ------------------------------------------------------------------ */
/* Página completa                                                    */
/* ------------------------------------------------------------------ */
export default function Landing() {
  const [open, setOpen] = useState(false);
  const [prefill, setPrefill] = useState(null);
  const demoRef = useRef(null);

  const openModal = (source = "landing") => {
    setPrefill({ source });
    setOpen(true);
  };
  const openModalPlan = (plan) => {
    setPrefill({ source: "plan", plan });
    setOpen(true);
  };
  const scrollToDemo = () => {
    demoRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  // Ids para nav anchors
  useEffect(() => {
    // anchors
    document.querySelector('[data-testid="lp-retos"]')?.setAttribute("id", "lp-retos");
    document.querySelector('[data-testid="lp-bloques"]')?.setAttribute("id", "lp-bloques");
    document.querySelector('[data-testid="lp-ia"]')?.setAttribute("id", "lp-ia");
    document.querySelector('[data-testid="lp-demo"]')?.setAttribute("id", "lp-demo");
  }, []);

  return (
    <div className="lp-root">
      <NavLP onCta={() => openModal("nav")} />
      <Hero onCta={() => openModal("hero")} onDemo={scrollToDemo} />
      <Retos />
      <QueEs />
      <Bloques />
      <SeccionIA onCta={scrollToDemo} />
      <Beneficios />
      <DemoFuncional ref={demoRef} />
      <ComoFunciona />
      <Planes onCta={openModalPlan} />
      <PorQue />
      <CtaBanda onCta={() => openModal("cta-band")} />
      <FooterLP />
      <CtaFinal open={open} prefill={prefill} onClose={() => setOpen(false)} />
    </div>
  );
}
